import { FormLabel } from '@/components/FormLabel'
import { Add } from '@/icons'
import { Button, Grid, HStack, Stack, Text } from '@chakra-ui/react'
import { InputFormControl, TextareaFormControl } from 'BrokerTsLib/src/components'
import type { AdditionSportEquipment, OfferProperty, OfferPropertyAddition, PackageItemModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { useEffect, useState } from 'react'
import { useFieldArray, useFormContext, type UseFieldArrayRemove } from 'react-hook-form'
import { VariantSelector } from '../AdditionValueEditor'

const getDefaultItem = (addon: PackageItemModel, limit: number) => {
    const defaultVariant = addon.variants.find(x => x.isDefault) ?? addon.variants[0]

    return {
        packageItemId: addon.packageItemId,
        variantId: defaultVariant?.id,
        limit: limit.toString(),
        riskId: addon.riskId,
    } as AdditionSportEquipment
}

export const SportEquipmentsProperty = ({
    offer,
    addon,
    part,
    onChange
}: {
    offer: OfferProperty
    addon: PackageItemModel
    part?: PartPackage
    onChange: (limit: number, variantId?: number) => void
}) => {
    const {
        control,
        setValue
    } = useFormContext<OfferPropertyAddition>()

    const name = 'sportEquipments'

    const { fields, append, remove, replace } = useFieldArray({
        control,
        name
    })

    const [limit, setLimit] = useState({ value: part?.limit ?? addon.variants.find(x => x.isDefault)?.defaultLimit ?? 0, packageItemVariantId: part?.packageItemVariantId })

    // vychozi nastaveni hodnot na OfferPropertyAddition z OfferPart
    useEffect(() => {
        fields.forEach((item, index) => {
            const part = offer.parts.find(x => x.partId == item.offerPartId)
            if (!part) return

            setValue(`${name}.${index}.riskId`, part.packageItem.riskId)
            setValue(`${name}.${index}.limit`, limit.value.toString())
            setValue(`${name}.${index}.variantId`, part.packageItemVariantId)
            setValue(`${name}.${index}.offerPartId`, part.partId)
            setValue(`${name}.${index}.packageItemId`, part.packageItem.packageItemId)
        })
    }, [addon, append, fields, limit.value, offer.parts, setValue])

    const maxItems = 10
    const limitThreshold = 50000
    const shouldSpecifySportEquipments = limit.value > limitThreshold

    const updateLimit = (limit: number, packageItemVariantId: number | undefined) => {
        setLimit({ value: limit, packageItemVariantId })

        const shouldSpecifySportEquipments = limit > limitThreshold
        if (shouldSpecifySportEquipments && fields.length == 0)
            append(getDefaultItem(addon, limit))

        if (!shouldSpecifySportEquipments && fields.length > 0)
            replace([])
    }

    return <Stack gap={4} w='full'>
        <HStack gap={4} justifyContent='space-between'>
            <VariantSelector
                limit={limit.value}
                packageItem={addon}
                packageItemVariantId={limit.packageItemVariantId}
                onSubmit={(limit, packageItemVariantId) => updateLimit(limit, packageItemVariantId)}
            />

            <Button
                colorScheme='green'
                onClick={() => {
                    onChange(limit.value, limit.packageItemVariantId)
                    console.log(limit.value)
                }}
            >
                Uložit
            </Button>
        </HStack>

        {shouldSpecifySportEquipments &&
            <HStack justifyContent='space-between'>
                <Text fontStyle='italic' fontSize='sm'>Pro sportovní vybavení s celkovou pořizovací cenou nad 50 000 Kč doplňte další údaje.</Text>

                <Text>{fields.length} / {maxItems}</Text>
            </HStack>
        }

        {shouldSpecifySportEquipments &&
            <Grid gridTemplateColumns={{ base: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={3}>
                {fields.map((field, index) => (
                    <SportEquipmentItem
                        key={field.id}
                        index={index}
                        remove={remove}
                    />
                ))}

                {fields.length < maxItems &&
                    <Stack>
                        <Button
                            variant='outline'
                            onClick={() => append(getDefaultItem(addon, limit.value))}
                        >
                            <Add color="secondary.500" />
                            Další
                        </Button>
                    </Stack>
                }
            </Grid>
        }
    </Stack>
}

type SportEquipmentItemProps = {
    index: number,
    remove: UseFieldArrayRemove
}

const SportEquipmentItem = ({
    index,
    remove
}: SportEquipmentItemProps) => {

    const name = 'sportEquipments'
    const { control } = useFormContext<OfferPropertyAddition>()

    return (
        <Stack
            border='1px solid' borderColor='gray.300' borderRadius='md' p={3}
            gap={3}
        >
            <FormLabel alignSelf='center'>{index + 1}. ks vybavení</FormLabel>

            <InputFormControl control={control}
                name={`${name}.${index}.type`}
                label='Typ a značka sportovního vybavení'
                placeholder='Např. kolo Favorit'
                formControlVariant='fullWidth'
                size='sm'
                labelProps={{ fontSize: 'sm' }}
                shouldUnregister
            />

            <TextareaFormControl control={control}
                name={`${name}.${index}.description`}
                label='Bližsí specifikace'
                placeholder='Např. výrobní číslo rámu/baterie, speciální úpravy, barva...'
                formControlVariant='fullWidth'
                size='sm'
                labelProps={{ fontSize: 'sm' }}
                shouldUnregister
            />

            <Button
                size='sm'
                colorScheme='secondary'
                variant='outline'
                onClick={() => remove(index)}
            >
                Odebrat
            </Button>
        </Stack>
    )
}
